import { CSSProperties, useCallback, useContext, useMemo, useState } from "react";
import Link from "../Link/Link";
import { IDssCardProps } from "./DssCard.interface";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { routes } from "../../shared/router/routes";
import { formatDate, formatTemplate, getFormattedPath } from "../../shared/helpers/utils";
import Modal from "../Modal/Modal";

const DssCard = ({ form, onHome }: IDssCardProps) => {
    const { lang, translate } = useContext(TranslationContext);
    const [isOpen, setIsOpen] = useState(false);
    const lastSubmission = useMemo(() => {
        const [lastSubmission] = form?.values ?? [];

        return lastSubmission
    }, [form]);

    const value = useMemo(() => lastSubmission?.score, [lastSubmission]);

    const scoreStyles = useCallback((value: number) => {
        if (value === undefined)
            return { bg: "bg-neutrals-white", main: "bg-blue-50", color: "color-blue-500", label: null };
        if (value > 79)
            return { bg: "bg-green-100", main: "bg-green-500", color: "color-neutrals-white", label: translate("dss.passed", "Maintain") };
        if (value > 59)
            return { bg: "bg-yellow-100", main: "bg-yellow-500", color: "color-neutrals-white", label: translate("dss.improve", "Attention") };
        if (value >= 0)
            return { bg: "bg-red-100", main: "bg-red-500", color: "color-neutrals-white", label: translate("dss.resolve", "Improve") };

        return { bg: "bg-neutrals-white", main: "bg-blue-50", color: "color-blue-500", label: null };
    }, [])

    const score = useMemo(() => scoreStyles(value), [value]);

    return (
        <article className={`card card_dss p-13 h-100 d-flex flex-column ${score.bg}`}>
            <div className="d-flex gap-02 gap-md-3 mb-22 justify-content-between align-items-start">
                <div className="d-flex flex-column gap-02">
                    <div className="d-flex gap-02 align-items-center flex-wrap">

                        <span className={`score ${score.main}`}></span>
                        {score.label && <span className="text-bd4-bd text-uppercase">{score.label}</span>}
                        <span className="text-bd4-bd color-neutrals-dark text-uppercase">
                            {lastSubmission?.date
                                ? formatTemplate(translate("dss.last_updated", "LAST UPDATED {0}"), [
                                    formatDate(lastSubmission.date, "dd MMMM YYYY", lang)
                                ])
                                : translate("dss.no_assessment_yet", "NO ASSESSMENT DONE YET")}
                        </span>
                    </div>
                    <h3 className="text-h4-md lineclamp-3 lineclamp-md-2 color-neutrals-black">
                        {form.title}
                    </h3>
                </div>
                {value !== undefined && <button
                    type="button"
                    onClick={() => setIsOpen(true)}
                    className={`history`}>
                    <i className="icon icon-Recently-viewed icon-32"></i>
                </button>}
            </div>
            {!onHome && (
                <Link
                    to={getFormattedPath(routes.DSS_FORM.path, { formId: form.formId })}
                    variant={value !== undefined ? "outlined" : "solid"}
                    customCss="w-100 mt-auto"
                    label={
                        value
                            ? translate("common.see_details", "See details")
                            : translate("dss.assess_your_workplace", "Assess your workplace")
                    }
                />
            )}
            {onHome && (
                <div className="d-flex gap-03 flex-wrap mt-auto">
                    {!!value && (
                        <Link
                            to={`${getFormattedPath(routes.DSS_FORM.path, { formId: form.formId })}?reassess=true`}
                            variant={value !== undefined ? "outlined" : "solid"}
                            customCss="d-none d-md-flex flex-grow-1 mt-auto"
                            label={translate("dss.reassess", "Reassess")}
                        />
                    )}
                    <Link
                        to={getFormattedPath(routes.DSS_FORM.path, { formId: form.formId })}
                        variant={value !== undefined ? "outlined" : "solid"}
                        customCss="d-none d-md-flex flex-grow-1 mt-auto"
                        label={
                            value
                                ? translate("common.see_details", "See details")
                                : translate("dss.assess_your_workplace", "Assess your workplace")
                        }
                    />
                    <Link
                        to={getFormattedPath(routes.DSS_FORM.path, { formId: form.formId })}
                        variant={value !== undefined ? "outlined" : "solid"}
                        customCss="d-md-none w-100"
                        label={
                            value
                                ? translate("common.see_details", "See details")
                                : translate("dss.assess_your_workplace", "Assess your workplace")
                        }
                    />
                </div>
            )}
            <Modal isOpen={isOpen} setIsOpen={setIsOpen} title={form.title} style={{ "--modal-width": "min(600px, 80vw)" } as CSSProperties}>
                <ul className="d-flex flex-column gap-1">
                    {form.values?.map((submit, i) =>
                        <li key={i} className="d-flex align-items-center gap-02">
                            <span className={`score ${scoreStyles(submit.score).main}`}></span>
                            <span>
                                <span className="text-bd2-rg color-neutrals-dark">{translate("generic.updated_on", "Updated on")} </span>
                                <span className="text-bd2-bd color-neutrals-dark text-uppercase">{formatDate(submit.date, "dd MMMM YYYY", lang)}</span>
                            </span>
                        </li>
                    )}
                </ul>
            </Modal>
        </article>
    );
};

export default DssCard;
